import type { CastAssignment, Personnel } from '../personnel/types';
import type { Character } from '../show-structure/types';

export interface ScheduleEventAttendeeExpansionContext {
  characters?: Character[];
  castAssignments?: CastAssignment[];
}

function addDeduped(contactIds: string[], seen: Set<string>, contactId: string): void {
  if (seen.has(contactId)) {
    return;
  }

  seen.add(contactId);
  contactIds.push(contactId);
}

function normalizeGroupKey(value: string): string {
  return value.toLowerCase().replace(/[^a-z0-9]/g, '');
}

function getAssignedPersonnelIdsForCharacters(
  characterIds: Set<string>,
  castAssignments: CastAssignment[] | undefined,
  activePersonnelIds: Set<string>
): string[] {
  if (!castAssignments || characterIds.size === 0) {
    return [];
  }

  const contactIds: string[] = [];
  const seen = new Set<string>();

  for (const assignment of castAssignments) {
    if (!characterIds.has(assignment.characterId) || !activePersonnelIds.has(assignment.personnelId)) {
      continue;
    }

    addDeduped(contactIds, seen, assignment.personnelId);
  }

  return contactIds;
}

function getCharacterIdsForCustomGroup(
  groupTag: string,
  characters: Character[] | undefined
): Set<string> {
  const groupName = groupTag.replace(/^group:/, '');
  const groupKey = normalizeGroupKey(groupName);
  const characterIds = new Set<string>();

  if (!characters || !groupKey) {
    return characterIds;
  }

  const activeCharacters = characters.filter((character) => !character.deleted);
  const ensembleIds = new Set(
    activeCharacters
      .filter((character) => character.isEnsemble)
      .map((character) => character.id)
  );

  if (groupKey === 'ensemble') {
    for (const character of activeCharacters) {
      if (character.isEnsemble || (character.parentId && ensembleIds.has(character.parentId))) {
        characterIds.add(character.id);
      }
    }
    return characterIds;
  }

  for (const character of activeCharacters) {
    const normalizedName = normalizeGroupKey(character.name);
    const normalizedId = normalizeGroupKey(character.id);
    const normalizedEnsembleName = normalizeGroupKey(character.name.replace(/^ensemble\s*/i, ''));

    if (
      normalizedName === groupKey
      || normalizedId === groupKey
      || (character.isEnsemble && normalizedEnsembleName === groupKey)
    ) {
      characterIds.add(character.id);
    }
  }

  return characterIds;
}

function getCustomGroupContactIds(
  groupTag: string,
  personnel: Personnel[],
  context: ScheduleEventAttendeeExpansionContext | undefined
): string[] {
  if (!groupTag.startsWith('group:')) {
    return [];
  }

  const characterIds = getCharacterIdsForCustomGroup(groupTag, context?.characters);
  const activePersonnelIds = new Set(
    personnel
      .filter((contact) => !contact.deleted)
      .map((contact) => contact.id)
  );

  return getAssignedPersonnelIdsForCharacters(
    characterIds,
    context?.castAssignments,
    activePersonnelIds
  );
}

export function expandScheduleEventAttendeeIds(
  attendees: string[],
  personnel: Personnel[],
  context?: ScheduleEventAttendeeExpansionContext
): string[] {
  const contactIds: string[] = [];
  const seen = new Set<string>();
  const addContact = (contactId: string) => addDeduped(contactIds, seen, contactId);

  for (const attendee of attendees) {
    switch (attendee) {
      case 'group:company':
        for (const contact of personnel) {
          if (!contact.deleted) {
            addContact(contact.id);
          }
        }
        break;
      case 'group:cast':
        for (const contact of personnel) {
          if (!contact.deleted && contact.category === 'cast') {
            addContact(contact.id);
          }
        }
        break;
      case 'group:crew':
        for (const contact of personnel) {
          if (!contact.deleted && contact.category === 'crew') {
            addContact(contact.id);
          }
        }
        break;
      case 'group:production':
        for (const contact of personnel) {
          if (
            !contact.deleted
            && (
              contact.category === 'production'
              || (contact.category === 'crew' && contact.isDepartmentHead === true)
            )
          ) {
            addContact(contact.id);
          }
        }
        break;
      default:
        if (attendee.startsWith('group:')) {
          const expandedContactIds = getCustomGroupContactIds(attendee, personnel, context);
          if (expandedContactIds.length > 0) {
            for (const contactId of expandedContactIds) {
              addContact(contactId);
            }
            break;
          }
        }

        addContact(attendee);
        break;
    }
  }

  return contactIds;
}
